import { Component, Input, OnChanges, QueryList, ViewChildren } from '@angular/core';
import { MapInfoWindow, MapMarker } from '@angular/google-maps';
import { ChartConfiguration, ChartDataset, ChartOptions, ChartType } from 'chart.js';
import { marked } from 'marked';
import { BaseChartDirective } from 'ng2-charts';
import * as Papa from 'papaparse';
import {
  ChatChartType,
  ChatVisualization,
  ChatVisualizationTable,
  ChatVisualizationResponse
} from '../../../models/chat-response.model';
import { ChatHeatPoint, createChatHeatmapOverlay } from './chat-heatmap-overlay';

const CHART_COLORS = [
  '#2870d8',
  '#188d52',
  '#f0a500',
  '#8d5bd6',
  '#d94f4f',
  '#00a8b5',
  '#f36f45',
  '#5fab61'
];

const CHART_EXPORT_WIDTH = 1600;

const MAP_CHART_TYPES: ChatChartType[] = ['map_points', 'map_connections', 'map_heatmap'];
const MAP_SINGLE_POINT_ZOOM = 15;
const MAP_MARKER_SCALE = 7;
const MAP_MARKER_MIN_SCALE = 5;
const MAP_MARKER_MAX_SCALE = 14;
const MAP_MAX_ABS_LATITUDE = 85.05112878;
const MAP_HEAT_MIN_WEIGHT = 0.4;

interface ChatVisualizationWithTable extends ChatVisualization {
  table?: {
    columns: string[];
    rows: any[][];
  };
}

interface ChatMapDetail {
  label: string;
  value: string;
}

interface ChatMapMarker {
  position: google.maps.LatLngLiteral;
  title: string;
  options: google.maps.MarkerOptions;
  details: ChatMapDetail[];
}

interface ChatMapConnection {
  path: google.maps.LatLngLiteral[];
  options: google.maps.PolylineOptions;
}

interface ChatMapLegendEntry {
  label: string;
  color: string;
}

interface ChatVisualizationMap {
  center: google.maps.LatLngLiteral;
  zoom: number;
  bounds: google.maps.LatLngBoundsLiteral | null;
  markers: ChatMapMarker[];
  connections: ChatMapConnection[];
  heatPoints: ChatHeatPoint[];
  legend: ChatMapLegendEntry[];
  unmappedRowCount: number;
  activeMarker: ChatMapMarker | null;
}

interface ChatVisualizationBlock {
  spec: ChatVisualizationWithTable;
  isMap: boolean;
  table: {
    columns: string[];
    rows: any[][];
  };
  chart: {
    type: ChartType;
    data: ChartConfiguration['data'];
    options: ChartOptions;
  } | null;
  map: ChatVisualizationMap | null;
  error?: string;
}

@Component({
  selector: 'lib-chat-visualization-response-message',
  templateUrl: './chat-visualization-response-message.component.html',
  styleUrls: ['./chat-visualization-response-message.component.scss'],
  standalone: false
})
export class ChatVisualizationResponseMessageComponent implements OnChanges {
  @Input() response!: ChatVisualizationResponse;
  @ViewChildren(BaseChartDirective) chartDirectives!: QueryList<BaseChartDirective>;

  visualizationBlocks: ChatVisualizationBlock[] = [];
  mapOptions: google.maps.MapOptions = {
    streetViewControl: false,
    mapTypeControl: false,
    fullscreenControl: false
  };

  ngOnChanges() {
    this.visualizationBlocks = this.createVisualizationBlocks();
  }

  private createVisualizationBlocks(): ChatVisualizationBlock[] {
    const data = this.response.data;
    const visualizations = (data.visualizations || []) as ChatVisualizationWithTable[];

    if (!visualizations.length) {
      return [];
    }

    return visualizations.map((spec) => {
      const table = spec.table || data.table;
      const isMap = MAP_CHART_TYPES.includes(spec.chart_type);

      if (isMap) {
        const mapResult = this.buildMap(spec, table);

        return {
          spec,
          isMap,
          table,
          chart: null,
          map: mapResult.map,
          error: mapResult.error
        };
      }

      const chartResult = this.buildChart(spec, table);

      return {
        spec,
        isMap,
        table,
        chart: chartResult.chart,
        map: null,
        error: chartResult.error
      };
    });
  }

  openMapMarker(
    infoWindow: MapInfoWindow,
    markerRef: MapMarker,
    mapData: ChatVisualizationMap,
    marker: ChatMapMarker
  ): void {
    mapData.activeMarker = marker;
    infoWindow.open(markerRef);
  }

  fitMapBounds(map: google.maps.Map, mapData: ChatVisualizationMap): void {
    if (mapData.bounds) {
      map.fitBounds(mapData.bounds, 24);
    }

    if (mapData.heatPoints.length) {
      createChatHeatmapOverlay(mapData.heatPoints).setMap(map);
    }
  }

  formatChartType(chartType: ChatChartType): string {
    return chartType.replace('_', ' ');
  }

  renderMarkdown(text: string): string {
    return marked.parse(text, {
      async: false,
      breaks: true,
      gfm: true
    });
  }

  formatCell(value: any): string {
    if (value === null || value === undefined) {
      return '-';
    }

    if (typeof value === 'number') {
      return Number.isInteger(value)
        ? value.toLocaleString()
        : value.toLocaleString(undefined, { maximumFractionDigits: 4 });
    }

    return String(value);
  }

  isNumericLike(value: any): boolean {
    if (value === null || value === undefined) {
      return false;
    }

    if (typeof value === 'number') {
      return true;
    }

    if (typeof value === 'string') {
      const stripped = value.replace(/,/g, '');
      return stripped !== '' && !Number.isNaN(Number(stripped));
    }

    return false;
  }

  hasDownloadableTable(table: ChatVisualizationTable | undefined): boolean {
    return !!table?.columns?.length && !!table?.rows?.length;
  }

  downloadCsv(table: ChatVisualizationTable, title: string = 'chat-visualization'): void {
    if (!this.hasDownloadableTable(table)) {
      return;
    }

    const records = table.rows.map((row) => {
      return table.columns.reduce<Record<string, any>>((record, column, index) => {
        record[column] = row[index] ?? '';
        return record;
      }, {});
    });

    const csv = Papa.unparse(records, {
      columns: table.columns,
      newline: '\r\n'
    });

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.href = url;
    link.download = `${this.toCsvFileName(title)}.csv`;
    link.click();

    URL.revokeObjectURL(url);
  }

  downloadChartImage(blockIndex: number, title: string = 'chat-visualization'): void {
    const sourceCanvas = this.getChartDirectiveForBlock(blockIndex)?.chart?.canvas;

    if (!sourceCanvas?.width || !sourceCanvas.height) {
      return;
    }

    const exportWidth = CHART_EXPORT_WIDTH;
    const exportHeight = Math.round((sourceCanvas.height / sourceCanvas.width) * exportWidth);
    const canvas = document.createElement('canvas');
    canvas.width = exportWidth;
    canvas.height = exportHeight;

    const context = canvas.getContext('2d');
    if (!context) {
      return;
    }

    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, canvas.width, canvas.height);
    context.imageSmoothingEnabled = true;
    context.imageSmoothingQuality = 'high';
    context.drawImage(sourceCanvas, 0, 0, canvas.width, canvas.height);

    const link = document.createElement('a');
    link.href = canvas.toDataURL('image/png');
    link.download = `${this.toCsvFileName(title)}.png`;
    link.click();
  }

  private getChartDirectiveForBlock(blockIndex: number): BaseChartDirective | undefined {
    const chartIndex = this.visualizationBlocks
      .slice(0, blockIndex + 1)
      .filter((block) => !!block.chart)
      .length - 1;

    return this.chartDirectives?.toArray()[chartIndex];
  }

  private buildMap(
    spec: ChatVisualizationWithTable,
    table: { columns: string[]; rows: any[][] }
  ): { map: ChatVisualizationMap | null; error?: string } {
    if (!table?.columns?.length || !table?.rows?.length) {
      return { map: null, error: 'No table data available for this map.' };
    }

    return spec.chart_type === 'map_connections'
      ? this.buildConnectionMap(spec, table)
      : this.buildPointMap(spec, table);
  }

  private buildPointMap(
    spec: ChatVisualizationWithTable,
    table: { columns: string[]; rows: any[][] }
  ): { map: ChatVisualizationMap | null; error?: string } {
    const columnIndex = this.createColumnIndex(table.columns);
    const latColumn = spec.dataset.lat;
    const lonColumn = spec.dataset.lon;

    if (!latColumn || !(latColumn in columnIndex) || !lonColumn || !(lonColumn in columnIndex)) {
      return { map: null, error: 'Latitude and longitude columns were not found in table.' };
    }

    const latIndex = columnIndex[latColumn];
    const lonIndex = columnIndex[lonColumn];
    const mappedRows = table.rows.filter((row) => this.isMappable(row[latIndex], row[lonIndex]));

    if (!mappedRows.length) {
      return { map: null, error: 'This result has no mappable coordinates.' };
    }

    const positions = mappedRows.map((row) => this.toPosition(row[latIndex], row[lonIndex]));
    const valueIndex = this.getOptionalColumnIndex(columnIndex, spec.dataset.value);
    const isHeatmap = spec.chart_type === 'map_heatmap';
    const seriesIndex = isHeatmap ? null : this.getOptionalColumnIndex(columnIndex, spec.dataset.series);
    const labelIndex = this.getOptionalColumnIndex(columnIndex, spec.dataset.label);
    const legend = seriesIndex === null ? [] : this.buildSeriesLegend(mappedRows, seriesIndex);
    const view = this.createMapView(positions);
    const markerScale = valueIndex === null
      ? null
      : this.createValueScale(mappedRows, valueIndex, MAP_MARKER_MIN_SCALE, MAP_MARKER_MAX_SCALE);
    const heatWeight = !isHeatmap
      ? null
      : (valueIndex === null
        ? () => 1
        : this.createValueScale(mappedRows, valueIndex, MAP_HEAT_MIN_WEIGHT, 1));

    return {
      map: {
        ...view,
        markers: isHeatmap ? [] : mappedRows.map((row, rowIndex) => ({
          position: positions[rowIndex],
          title: labelIndex === null ? spec.title : this.formatCell(row[labelIndex]),
          options: this.createMarkerOptions(
            markerScale ? markerScale(this.toNumber(row[valueIndex!])) : MAP_MARKER_SCALE,
            seriesIndex === null ? CHART_COLORS[0] : this.getLegendColor(legend, this.formatCell(row[seriesIndex]))
          ),
          details: this.createRowDetails(table.columns, row)
        })),
        connections: [],
        heatPoints: !heatWeight ? [] : mappedRows.map((row, rowIndex) => ({
          position: positions[rowIndex],
          weight: heatWeight(valueIndex === null ? 0 : this.toNumber(row[valueIndex]))
        })),
        legend,
        unmappedRowCount: table.rows.length - mappedRows.length,
        activeMarker: null
      }
    };
  }

  private buildConnectionMap(
    spec: ChatVisualizationWithTable,
    table: { columns: string[]; rows: any[][] }
  ): { map: ChatVisualizationMap | null; error?: string } {
    const columnIndex = this.createColumnIndex(table.columns);
    const endpointColumns = [
      spec.dataset.from_lat,
      spec.dataset.from_lon,
      spec.dataset.to_lat,
      spec.dataset.to_lon
    ];

    if (endpointColumns.some((column) => !column || !(column in columnIndex))) {
      return { map: null, error: 'Connection endpoint columns were not found in table.' };
    }

    const [fromLatIndex, fromLonIndex, toLatIndex, toLonIndex] =
      endpointColumns.map((column) => columnIndex[column!]);
    const mappedRows = table.rows.filter((row) =>
      this.isMappable(row[fromLatIndex], row[fromLonIndex]) && this.isMappable(row[toLatIndex], row[toLonIndex]));

    if (!mappedRows.length) {
      return { map: null, error: 'This result has no mappable coordinates.' };
    }

    const fromLabelIndex = this.getOptionalColumnIndex(columnIndex, spec.dataset.from_label);
    const toLabelIndex = this.getOptionalColumnIndex(columnIndex, spec.dataset.to_label);
    const positions: google.maps.LatLngLiteral[] = [];
    const connections: ChatMapConnection[] = [];
    const endpointMarkers = new Map<string, ChatMapMarker>();

    mappedRows.forEach((row) => {
      const from = this.toPosition(row[fromLatIndex], row[fromLonIndex]);
      const to = this.toPosition(row[toLatIndex], row[toLonIndex]);

      positions.push(from, to);
      connections.push({
        path: [from, to],
        options: {
          strokeColor: CHART_COLORS[0],
          strokeOpacity: 0.8,
          strokeWeight: 2
        }
      });

      this.addEndpointMarker(endpointMarkers, from, fromLabelIndex, spec, table.columns, row);
      this.addEndpointMarker(endpointMarkers, to, toLabelIndex, spec, table.columns, row);
    });

    return {
      map: {
        ...this.createMapView(positions),
        markers: [...endpointMarkers.values()],
        connections,
        heatPoints: [],
        legend: [],
        unmappedRowCount: table.rows.length - mappedRows.length,
        activeMarker: null
      }
    };
  }

  private addEndpointMarker(
    markers: Map<string, ChatMapMarker>,
    position: google.maps.LatLngLiteral,
    labelIndex: number | null,
    spec: ChatVisualizationWithTable,
    columns: string[],
    row: any[]
  ): void {
    const key = `${position.lat},${position.lng}`;

    if (markers.has(key)) {
      return;
    }

    markers.set(key, {
      position,
      title: labelIndex === null ? spec.title : this.formatCell(row[labelIndex]),
      options: this.createMarkerOptions(MAP_MARKER_MIN_SCALE, CHART_COLORS[0]),
      details: this.createRowDetails(columns, row)
    });
  }

  private createMarkerOptions(scale: number, fillColor: string): google.maps.MarkerOptions {
    return {
      icon: {
        path: google.maps.SymbolPath.CIRCLE,
        scale,
        fillColor,
        fillOpacity: 0.9,
        strokeColor: '#ffffff',
        strokeWeight: 1
      }
    };
  }

  private createMapView(
    positions: google.maps.LatLngLiteral[]
  ): Pick<ChatVisualizationMap, 'center' | 'zoom' | 'bounds'> {
    const south = positions.reduce((value, position) => Math.min(value, position.lat), positions[0].lat);
    const north = positions.reduce((value, position) => Math.max(value, position.lat), positions[0].lat);
    const west = positions.reduce((value, position) => Math.min(value, position.lng), positions[0].lng);
    const east = positions.reduce((value, position) => Math.max(value, position.lng), positions[0].lng);
    const isSinglePoint = north - south < 1e-6 && east - west < 1e-6;

    return {
      center: { lat: (south + north) / 2, lng: (west + east) / 2 },
      zoom: MAP_SINGLE_POINT_ZOOM,
      bounds: isSinglePoint ? null : { north, south, east, west }
    };
  }

  private createValueScale(
    rows: any[][],
    valueIndex: number,
    minOutput: number,
    maxOutput: number
  ): (value: number) => number {
    const values = rows.map((row) => this.toNumber(row[valueIndex]));
    const min = values.reduce((value, current) => Math.min(value, current), values[0]);
    const max = values.reduce((value, current) => Math.max(value, current), values[0]);
    const range = max - min;

    if (range <= 0) {
      return () => (minOutput + maxOutput) / 2;
    }

    return (value) => minOutput + ((value - min) / range) * (maxOutput - minOutput);
  }

  private buildSeriesLegend(rows: any[][], seriesIndex: number): ChatMapLegendEntry[] {
    return [...new Set(rows.map((row) => this.formatCell(row[seriesIndex])))]
      .map((label, index) => ({
        label,
        color: CHART_COLORS[index % CHART_COLORS.length]
      }));
  }

  private getLegendColor(legend: ChatMapLegendEntry[], label: string): string {
    return legend.find((entry) => entry.label === label)?.color ?? CHART_COLORS[0];
  }

  private createRowDetails(columns: string[], row: any[]): ChatMapDetail[] {
    return columns.map((column, index) => ({
      label: column,
      value: this.formatCell(row[index])
    }));
  }

  private getOptionalColumnIndex(columnIndex: Record<string, number>, column: string | null): number | null {
    return column && column in columnIndex ? columnIndex[column] : null;
  }

  private isMappable(latitude: any, longitude: any): boolean {
    if (!this.isNumericLike(latitude) || !this.isNumericLike(longitude)) {
      return false;
    }

    const lat = this.toNumber(latitude);
    const lon = this.toNumber(longitude);

    // Web Mercator is undefined past +-85.05 degrees. A row at the pole makes
    // fitBounds pin the viewport there, which blanks the whole map.
    if (Math.abs(lat) > MAP_MAX_ABS_LATITUDE || Math.abs(lon) > 180) {
      return false;
    }

    return lat !== 0 || lon !== 0;
  }

  private toPosition(latitude: any, longitude: any): google.maps.LatLngLiteral {
    return {
      lat: this.toNumber(latitude),
      lng: this.toNumber(longitude)
    };
  }

  private buildChart(
    spec: ChatVisualizationWithTable,
    table: { columns: string[]; rows: any[][] }
  ): { chart: ChatVisualizationBlock['chart']; error?: string } {
    if (!table?.columns?.length || !table?.rows?.length) {
      return { chart: null, error: 'No table data available for this chart.' };
    }

    if (spec.chart_type === 'none') {
      return { chart: null };
    }

    if (spec.chart_type === 'gauge') {
      return this.buildGaugeChart(spec, table);
    }

    if (spec.chart_type === 'pareto') {
      return this.buildParetoChart(spec, table);
    }

    return this.buildStandardChart(spec, table);
  }

  private buildStandardChart(
    spec: ChatVisualizationWithTable,
    table: { columns: string[]; rows: any[][] }
  ): { chart: ChatVisualizationBlock['chart']; error?: string } {
    const columnIndex = this.createColumnIndex(table.columns);
    const xColumn = spec.dataset.x;
    const yColumns = spec.dataset.y || [];
    const seriesColumn = spec.dataset.series;

    if (!(xColumn in columnIndex)) {
      return { chart: null, error: `Column "${xColumn}" not found in table.` };
    }

    const missingYColumn = yColumns.find((column) => !(column in columnIndex));
    if (missingYColumn) {
      return { chart: null, error: `Column "${missingYColumn}" not found in table.` };
    }

    const xIndex = columnIndex[xColumn];
    const labels = [...new Set(table.rows.map((row) => String(row[xIndex])))];
    const isHorizontal = spec.chart_type === 'bar_horizontal';
    const isStacked = spec.chart_type === 'bar_stacked';
    const isMultiLine = spec.chart_type === 'line_multi';
    const chartType: ChartType = spec.chart_type === 'pie' ? 'pie'
      : spec.chart_type.startsWith('bar') ? 'bar'
        : 'line';

    let datasets: ChartDataset[] = [];

    if ((isStacked || isMultiLine) && seriesColumn && seriesColumn in columnIndex) {
      const seriesIndex = columnIndex[seriesColumn];
      const yIndex = columnIndex[yColumns[0]];
      const seriesValues = [...new Set(table.rows.map((row) => String(row[seriesIndex])))];

      datasets = seriesValues.map((seriesValue, index) => ({
        label: seriesValue,
        data: labels.map((label) => {
          const row = table.rows.find(
            (currentRow) => String(currentRow[xIndex]) === label && String(currentRow[seriesIndex]) === seriesValue
          );
          return row ? this.toNumber(row[yIndex]) : 0;
        }),
        backgroundColor: this.withAlpha(CHART_COLORS[index % CHART_COLORS.length], isStacked ? 0.8 : 0),
        borderColor: CHART_COLORS[index % CHART_COLORS.length],
        borderWidth: isMultiLine ? 2 : 1,
        fill: false,
        tension: 0.3,
        pointRadius: 3
      }));
    } else {
      datasets = yColumns.map((yColumn, index) => {
        const yIndex = columnIndex[yColumn];
        const isLine = chartType === 'line';

        return {
          label: yColumn,
          data: table.rows.map((row) => this.toNumber(row[yIndex])),
          backgroundColor: chartType === 'pie'
            ? table.rows.map((_, rowIndex) => CHART_COLORS[rowIndex % CHART_COLORS.length])
            : this.withAlpha(CHART_COLORS[index % CHART_COLORS.length], isLine ? 0 : 0.6),
          borderColor: CHART_COLORS[index % CHART_COLORS.length],
          borderWidth: isLine ? 2 : 1,
          fill: false,
          tension: 0.3,
          pointRadius: 3
        };
      });
    }

    return {
      chart: {
        type: chartType,
        data: { labels, datasets },
        options: this.createChartOptions(spec, {
          isHorizontal,
          isStacked,
          showLegend: yColumns.length > 1 || chartType === 'pie' || isMultiLine || isStacked,
          valueFormat: spec.options?.value_format
        })
      }
    };
  }

  private buildParetoChart(
    spec: ChatVisualizationWithTable,
    table: { columns: string[]; rows: any[][] }
  ): { chart: ChatVisualizationBlock['chart']; error?: string } {
    const columnIndex = this.createColumnIndex(table.columns);
    const xColumn = spec.dataset.x;
    const yColumn = spec.dataset.y?.[0];

    if (!(xColumn in columnIndex) || !yColumn || !(yColumn in columnIndex)) {
      return { chart: null, error: 'Required Pareto columns were not found in table.' };
    }

    const xIndex = columnIndex[xColumn];
    const yIndex = columnIndex[yColumn];
    const sortedRows = [...table.rows].sort((a, b) => this.toNumber(b[yIndex]) - this.toNumber(a[yIndex]));
    const labels = sortedRows.map((row) => String(row[xIndex]));
    const values = sortedRows.map((row) => this.toNumber(row[yIndex]));
    const total = values.reduce((sum, value) => sum + value, 0);
    let cumulative = 0;
    const cumulativePercent = values.map((value) => {
      cumulative += value;
      return total > 0 ? Number(((cumulative / total) * 100).toFixed(1)) : 0;
    });

    return {
      chart: {
        type: 'bar',
        data: {
          labels,
          datasets: [
            {
              type: 'bar',
              label: yColumn,
              data: values,
              backgroundColor: this.withAlpha(CHART_COLORS[0], 0.65),
              borderColor: CHART_COLORS[0],
              borderWidth: 1,
              yAxisID: 'yLeft'
            },
            {
              type: 'line',
              label: 'Cumulative %',
              data: cumulativePercent,
              borderColor: CHART_COLORS[2],
              borderWidth: 2,
              fill: false,
              pointRadius: labels.length > 30 ? 0 : 3,
              tension: 0,
              yAxisID: 'yRight'
            }
          ]
        },
        options: this.createParetoOptions(spec, yColumn)
      }
    };
  }

  private buildGaugeChart(
    spec: ChatVisualizationWithTable,
    table: { columns: string[]; rows: any[][] }
  ): { chart: ChatVisualizationBlock['chart']; error?: string } {
    const columnIndex = this.createColumnIndex(table.columns);
    const yColumn = spec.dataset.y?.[0];

    if (!yColumn || !(yColumn in columnIndex)) {
      return { chart: null, error: `Column "${yColumn || ''}" not found.` };
    }

    const value = this.toNumber(table.rows[0][columnIndex[yColumn]]);
    const gauge = spec.options?.gauge;
    const min = gauge?.min ?? 0;
    const max = gauge?.max ?? 100;
    const range = max - min || 100;
    const clampedValue = Math.min(Math.max(value, min), max);
    const filled = clampedValue - min;
    const remaining = Math.max(max - clampedValue, 0);
    const color = this.getGaugeColor(value, gauge?.thresholds);
    const formattedValue = spec.options?.value_format === 'percent'
      ? `${value.toFixed(1)}%`
      : this.formatLargeNumber(value);

    return {
      chart: {
        type: 'doughnut',
        data: {
          labels: [formattedValue, 'Remaining'],
          datasets: [
            {
              data: [filled, remaining || range * 0.0001],
              backgroundColor: [color, '#dce5df'],
              borderColor: ['transparent', 'transparent'],
              circumference: 180,
              rotation: 270,
              cutout: '72%'
            } as ChartDataset<'doughnut'>
          ]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { display: false },
            tooltip: {
              callbacks: {
                label: () => `${yColumn}: ${formattedValue}`
              }
            }
          }
        }
      }
    };
  }

  private createChartOptions(
    spec: ChatVisualizationWithTable,
    settings: {
      isHorizontal: boolean;
      isStacked: boolean;
      showLegend: boolean;
      valueFormat?: string;
    }
  ): ChartOptions {
    return {
      indexAxis: settings.isHorizontal ? 'y' : 'x',
      responsive: true,
      maintainAspectRatio: false,
      interaction: {
        mode: 'index',
        intersect: false
      },
      plugins: {
        legend: {
          display: settings.showLegend,
          labels: { color: '#4d5a55' }
        },
        tooltip: {
          callbacks: {
            label: (context) => {
              const value = settings.isHorizontal ? context.parsed.x : context.parsed.y;
              return `${context.dataset.label}: ${this.formatChartValue(value, settings.valueFormat)}`;
            }
          }
        }
      },
      scales: {
        x: {
          stacked: settings.isStacked,
          ticks: {
            color: '#5f6b66',
            maxRotation: 45,
            autoSkip: true,
            maxTicksLimit: 20,
            callback: function (value) {
              return settings.isHorizontal
                ? value
                : this.getLabelForValue(Number(value));
            }
          },
          grid: { color: '#e9efec' },
          title: {
            display: !!spec.options?.x_axis_label,
            text: spec.options?.x_axis_label,
            color: '#5f6b66'
          }
        },
        y: {
          stacked: settings.isStacked,
          ticks: {
            color: '#5f6b66',
            callback: (value) => settings.isHorizontal ? value : this.formatLargeNumber(Number(value))
          },
          grid: { color: '#e9efec' },
          title: {
            display: !!spec.options?.y_axis_label,
            text: spec.options?.y_axis_label,
            color: '#5f6b66'
          }
        }
      }
    };
  }

  private createParetoOptions(spec: ChatVisualizationWithTable, yColumn: string): ChartOptions {
    return {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: true,
          labels: { color: '#4d5a55' }
        },
        tooltip: {
          callbacks: {
            label: (context) => {
              if (context.dataset.yAxisID === 'yRight') {
                return `Cumulative: ${context.parsed.y}%`;
              }

              return `${context.dataset.label}: ${this.formatLargeNumber(context.parsed.y)}`;
            }
          }
        }
      },
      scales: {
        x: {
          ticks: {
            color: '#5f6b66',
            maxRotation: 45,
            autoSkip: true,
            maxTicksLimit: 20
          },
          grid: { color: '#e9efec' }
        },
        yLeft: {
          type: 'linear',
          position: 'left',
          ticks: {
            color: '#5f6b66',
            callback: (value) => this.formatLargeNumber(Number(value))
          },
          grid: { color: '#e9efec' },
          title: {
            display: true,
            text: spec.options?.y_axis_label || yColumn,
            color: '#5f6b66'
          }
        },
        yRight: {
          type: 'linear',
          position: 'right',
          min: 0,
          max: 100,
          ticks: {
            color: '#5f6b66',
            callback: (value) => `${value}%`
          },
          grid: { drawOnChartArea: false },
          title: {
            display: true,
            text: 'Cumulative %',
            color: '#5f6b66'
          }
        }
      }
    };
  }

  private createColumnIndex(columns: string[]): Record<string, number> {
    return columns.reduce<Record<string, number>>((index, column, columnIndex) => {
      index[column] = columnIndex;
      return index;
    }, {});
  }

  private toNumber(value: any): number {
    if (typeof value === 'number') {
      return value;
    }

    const parsed = Number.parseFloat(String(value ?? '').replace(/,/g, ''));
    return Number.isNaN(parsed) ? 0 : parsed;
  }

  private formatLargeNumber(value: number): string {
    const absoluteValue = Math.abs(value);

    if (absoluteValue >= 1e12) {
      return `${(value / 1e12).toFixed(2)} T`;
    }

    if (absoluteValue >= 1e9) {
      return `${(value / 1e9).toFixed(2)} B`;
    }

    if (absoluteValue >= 1e6) {
      return `${(value / 1e6).toFixed(2)} M`;
    }

    if (absoluteValue >= 1e3) {
      return `${(value / 1e3).toFixed(2)} K`;
    }

    return value.toLocaleString();
  }

  private formatChartValue(value: number, format?: string): string {
    if (format === 'percent') {
      return `${value.toFixed(1)}%`;
    }

    if (format === 'integer') {
      return Math.round(value).toLocaleString();
    }

    return this.formatLargeNumber(value);
  }

  private getGaugeColor(value: number, thresholds?: any[][]): string {
    if (!thresholds?.length) {
      return CHART_COLORS[0];
    }

    for (const [threshold, color] of thresholds) {
      if (value <= this.toNumber(threshold)) {
        return String(color);
      }
    }

    return CHART_COLORS[0];
  }

  private withAlpha(hexColor: string, alpha: number): string {
    const normalizedHex = hexColor.replace('#', '');
    const red = Number.parseInt(normalizedHex.substring(0, 2), 16);
    const green = Number.parseInt(normalizedHex.substring(2, 4), 16);
    const blue = Number.parseInt(normalizedHex.substring(4, 6), 16);

    return `rgba(${red}, ${green}, ${blue}, ${alpha})`;
  }

  private toCsvFileName(value: string): string {
    const normalized = (value || 'chat-visualization')
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');

    return normalized || 'chat-visualization';
  }
}
